import { css, keyframes } from "goober";
import { useEffect, useRef, useState } from "react";
import {
  clearBranchAwaitingAiName,
  isBranchAwaitingAiName,
} from "../lib/aiBranchNames";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeInChar = css`
  animation: ${fadeIn} 120ms ease-out;
`;

interface Char {
  char: string;
  key: number;
  animate: boolean;
}

const REVEAL_MS = 20;

export function StreamingText({
  text,
  branchId,
}: {
  text: string;
  branchId: string;
}) {
  const shouldAnimateRef = useRef(isBranchAwaitingAiName(branchId));
  // oxlint-disable-next-line react/jsx-no-useless-fragment -- component must return JSX
  if (!shouldAnimateRef.current) return <>{text}</>;
  return <AnimatedStreamingText text={text} branchId={branchId} />;
}

function AnimatedStreamingText({
  text,
  branchId,
}: {
  text: string;
  branchId: string;
}) {
  const keyRef = useRef(0);
  const textRef = useRef(text);
  const [displayed, setDisplayed] = useState<Char[]>(() =>
    text.split("").map((char) => ({
      char,
      key: keyRef.current++,
      animate: false,
    }))
  );

  useEffect(() => {
    textRef.current = text;
  }, [text]);

  useEffect(() => {
    const id = setInterval(() => {
      setDisplayed((current) => {
        const target = textRef.current;

        let prefixLen = 0;
        while (
          prefixLen < target.length &&
          prefixLen < current.length &&
          target[prefixLen] === current[prefixLen]?.char
        ) {
          prefixLen++;
        }
        if (prefixLen < current.length) return current.slice(0, prefixLen);
        const nextChar = target[current.length];
        if (nextChar !== undefined) {
          const next = [
            ...current,
            { char: nextChar, key: keyRef.current++, animate: true },
          ];
          if (next.length === target.length) {
            clearBranchAwaitingAiName(branchId);
          }
          return next;
        }
        return current;
      });
    }, REVEAL_MS);
    return () => clearInterval(id);
  }, [branchId]);

  return (
    <>
      {displayed.map(({ char, key, animate }) => (
        <span key={key} className={animate ? fadeInChar : ""}>
          {char}
        </span>
      ))}
    </>
  );
}
